import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from '@reach/router'
import MessageIcon from '../assets/MessageIcon'
import { useFormContext } from '../context/form.context'
import { useLangContext } from '../context/lang.context'
import {
  s,
  sInput,
  sTextArea,
  sInputError,
} from '../style'
import Headline from './Headline'
import Grid from './Grid'
import { validateEmail } from '../utils'
import FormAgreements, { agreementsContent } from './FormAgreements'
import AdministrativeFormInfo from './AdministrativeFormInfo'
import FormFeedback from './FormFeedback'
import Button from './Button'

import axios from 'axios'
import 'react-google-places-autocomplete/dist/index.min.css'

const FormDynamic = ({ popup, notypes, override }) => {
  const location = useLocation()
  const { lang } = useLangContext()
  const {
    formType: formTypeGlobal,
    setFormOpen,
    setFormType: setFormTypeGlobal,
  } = useFormContext()
  const [formType, setFormType] = useState(override || formTypeGlobal)
  const currentForm = types.find((form) => form.type === formType)

  const [formAgreements, setFormAgreements] = useState([])

  const initialFormData = {
    message: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    company: '',
    nip: '',
    agreements: [],
  }

  const createInitialAgreements = (agreements) => {
    return agreements.map(agreement => ({
      subAgreements: agreement.subAgreements.map(sub => {
        if (sub.options) {
          return {
            values: Array(sub.options.length).fill(false)
          }
        }
        return false
      })
    }))
  }

  const [formData, setFormData] = useState(initialFormData)
  const [mailState, setMailState] = useState('unfilled')
  const [success, setSuccess] = useState(null)
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [wasSent, setWasSent] = useState(false)
  const formDataEmail = formData.email

  // Initialize formAgreements on component mount
  useEffect(() => {
    const initialAgreements = createInitialAgreements(agreementsContent.agreements)
    setFormAgreements(initialAgreements)
  }, [])

  // **New UseEffect to Reset Agreements When formType Changes**
  useEffect(() => {
    const initialAgreements = createInitialAgreements(agreementsContent.agreements)
    setFormAgreements(initialAgreements)
  }, [formType])

  const isRequiredFilled = useCallback(() => {

    return Object.entries(content.fields)
      .map(([key, value]) => {
        if (value.isRequired?.includes(formType)) {
          const fieldData = formData[key]
          if (typeof fieldData === 'string') {
            return fieldData.trim().length > 0
          } else if (Array.isArray(fieldData)) {
            return fieldData.length > 0
          }
        }
        return true
      })
      .reduce((acc, curr) => acc && curr, true)
  }, [formData, formType])

  const agreementFilled = formAgreements[0]

  // Extended validate
  const validate = useCallback(() => {
    const missingFields = []

    if (!isRequiredFilled()) missingFields.push('required fields not filled')
    if (!validateEmail(formDataEmail)) missingFields.push('invalid email')
    if (!agreementFilled) missingFields.push('agreements not checked')

    if (missingFields.length > 0) {
      // console.log('Form validation failed:', missingFields);
      return false
    }

    return true
  }, [isRequiredFilled, formDataEmail, agreementFilled])

  const onValueChange = (event) => {
    // Handle standard HTML inputs
    if (event.target) {
      setFormData({
        ...formData,
        [event.target.name]: event.target.value,
      })
    }
  }

  const sendMail = async () => {
    setWasSent(true)

    if (validate()) {
      setSuccess(null)
      setOpenBackdrop(true)
      setMailState('loading')

      if (formData.email.includes("@omida.pl") || formData.email.includes("@seaandair.pl")) {} else {
        axios({
          method: 'POST',
          url:
              process.env.NODE_ENV === 'development'
                  ? 'http://localhost:4000/hubspot-omidatrade/init'
                  : 'https://cleverserver.appclever.pl/hubspot-omidatrade/init',
          data: {
            'website': location.href,
            ...formData,
            lang
          },
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
        }).then((res) => {
          if (res.status === 200) {
            // console.log(res)

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'order_submission',
              spedify_data: {
                place: popup ? 'popup' : 'page',
                type: formType,
                service: formData.service,
              },
            })

            setOpenBackdrop(true)
            setFormData(initialFormData)
            setFormAgreements(createInitialAgreements(agreementsContent.agreements));
            setSuccess(true)
            setWasSent(false)
          }
        }).catch((error) => {
          // console.log(error)
        })
      }

      axios({
        method: 'POST',

        url:
          process.env.NODE_ENV === 'development'
            ? 'http://localhost:5000/omida/newsletter/init'
            : 'https://cleverserver.appclever.pl/trade/newsletter/init',
        data: {
          ...formData,
          campaignToken: formType === 'CARRIER' ? 'ZcQUm' : 'f9Fzo',
          formSent: true,
          lang,
        },
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      })
        .then((res) => {
          if (res.status === 200) {
            setOpenBackdrop(true)
            setFormData(initialFormData)
            setFormAgreements(createInitialAgreements(agreementsContent.agreements));
            setSuccess(true)
            setWasSent(false)
          }
        })
        .catch((error) => {
          console.log(error)
          setOpenBackdrop(true)
          setSuccess(false)
        })
    }
  }

  const onEscape = (event) => {
    if (event.keyCode === 27) {
      setFormOpen(false)
    }
  }

  useEffect(() => {
    if (validate()) setMailState('filled')
    else setMailState('unfilled')
  }, [validate, setMailState])

  useEffect(() => {
    setWasSent(false)
    if (!override) setFormType(formTypeGlobal)
  }, [formTypeGlobal, override])

  /* eslint-disable */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', onEscape, false)
    }
  }, [])

  useEffect(() => {
    if (override) setFormType(override)
  }, [override])
  /* eslint-enable */

  return (
    <>
      <div css={sContainer}>
        <Headline
          medium
          gradient
          maxWidth
          title={currentForm.title[lang]}
          desc={currentForm.desc[lang]}
        />
        <Grid container ccss={sFormContainer}>
          <Grid item xs={12} md={6} ccss={sPadding(true)}>
            <input
              name='firstName'
              type={content.fields.firstName.type}
              label={content.fields.firstName.label[lang]}
              placeholder={content.fields.firstName.label[lang]}
              required={content.fields.firstName.isRequired.includes(formType)}
              onChange={onValueChange}
              value={formData['firstName']}
              css={sInput}
            />
          </Grid>
          <Grid item xs={12} md={6} ccss={{ [s.md]: { padding: '0 0.75rem' } }}>
            <input
              name='lastName'
              type={content.fields.lastName.type}
              label={content.fields.lastName.label[lang]}
              placeholder={content.fields.lastName.label[lang]}
              required={content.fields.lastName.isRequired.includes(formType)}
              onChange={onValueChange}
              value={formData['lastName']}
              css={sInput}
            />
          </Grid>
          <Grid item xs={12} md={6} ccss={sPadding(true)}>
            {(() => {
              const isRequired =
                content.fields.company.isRequired.includes(formType)
              return (
                <input
                  name='company'
                  type={content.fields.company.type}
                  label={content.fields.company.label[lang]}
                  placeholder={`${content.fields.company.label[lang]} ${
                    isRequired ? ' *' : ''
                  }`}
                  required={isRequired}
                  onChange={onValueChange}
                  value={formData['company']}
                  css={[
                    sInput,
                    wasSent && isRequired && !formData.company && sInputError,
                  ]}
                />
              )
            })()}
          </Grid>
          {(() => {
            const isRequired = content.fields.nip.isRequired.includes(formType)
            return (
              <Grid item xs={12} md={6} ccss={sPadding(false)}>
                <input
                  name='nip'
                  type={content.fields.nip.type}
                  label={content.fields.nip.label[lang]}
                  placeholder={`${content.fields.nip.label[lang]} ${
                    isRequired ? ' *' : ''
                  }`}
                  required={isRequired}
                  onChange={onValueChange}
                  value={formData['nip']}
                  css={[
                    sInput,
                    wasSent && isRequired && !formData.nip && sInputError,
                  ]}
                />
              </Grid>
            )
          })()}

          {(() => {
            const isRequired =
              content.fields.email.isRequired.includes(formType)
            return (
              <Grid
                item
                xs={12}
                md={6}
                ccss={[{ height: 'max-content' }, sPadding(true)]}>
                <div css={{ position: 'relative', width: '100%' }}>
                  <input
                    name='email'
                    type='text'
                    label={content.fields.email.label[lang]}
                    placeholder={`${content.fields.email.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`}
                    required={isRequired}
                    onChange={onValueChange}
                    value={formData['email']}
                    css={[
                      sInput,
                      wasSent &&
                        isRequired &&
                        !validateEmail(formDataEmail) &&
                        sInputError,
                    ]}
                  />
                  {formDataEmail !== '' && !validateEmail(formDataEmail) && (
                    <div
                      css={{
                        position: 'absolute',
                        top: 16,
                        right: 32,
                        // transform: 'translateY(-50%)',
                        color: 'red',
                      }}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        height='24'
                        width='24'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path
                          fillRule='evenodd'
                          d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </Grid>
            )
          })()}
          <Grid item xs={12} md={6} ccss={sPadding(false)}>
            <input
              name='phone'
              type={content.fields.phone.type}
              label={content.fields.phone.label[lang]}
              placeholder={`${content.fields.phone.label[lang]} ${
                content.fields.phone.isRequired.includes(formType) ? ' *' : ''
              }`}
              required={content.fields.phone.isRequired.includes(formType)}
              onChange={onValueChange}
              value={formData['phone']}
              css={sInput}
            />
          </Grid>
          {(() => {
            const isRequired =
              content.fields.message.isRequired.includes(formType)
            return (
              <Grid item xs={12} ccss={{ height: '100%', flexGrow: 1 }}>
                <div css={{ width: '100%' }}>
                  <textarea
                    name='message'
                    type='text'
                    placeholder={`${content.fields.message.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`}
                    required={isRequired}
                    onChange={onValueChange}
                    value={formData['message']}
                    rows={3}
                    css={[
                      sInput,
                      sTextArea,
                      wasSent && isRequired && !formData.message && sInputError,
                    ]}
                  />
                </div>
              </Grid>
            )
          })()}
        </Grid>
        <FormAgreements
          formAgreements={formAgreements}
          setFormAgreements={setFormAgreements}
          wasSent={wasSent}
          content={agreementsContent}
          formType={formType}
          onAgreementsChange={(agreementData) => {
            setFormData(prevData => ({
              ...prevData,
              agreements: agreementData
            }));
          }}
        />
        <AdministrativeFormInfo lang={lang} />
        <div
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1.5rem',
              alignItems: 'center',
              width: '100%',
            },
            popup && { position: 'sticky', bottom: 0, left: 0 },
          ]}>
          <Button
            noMargin
            center
            reactiveBlack
            active={wasSent ? mailState === 'filled' : true}
            googleId={mailState === 'filled' ? 'send_form' : 'fill_form'}
            dataFormType={formType}
            action={sendMail}>
            {wasSent && mailState !== 'filled'
              ? !agreementFilled
                ? content.dictonary.fillagreements[lang]
                : content.dictonary.fill[lang]
              : content.dictonary.send[lang]}
          </Button>
        </div>
      </div>
      {openBackdrop && (
        <FormFeedback
          popup={popup}
          success={success}
          open={openBackdrop}
          setOpen={setOpenBackdrop}
          dataSuccess={content.dictonary.success}
          dataFailure={content.dictonary.failure}
        />
      )}
    </>
  )
}

export const sContainer = {
  width: '100%',
  height: 'max-content',
  display: 'flex',
  flexDirection: 'column',
}

export const sFormContainer = {
  marginTop: '4rem',
  [s.sm_down]: {
    marginTop: '2rem',
  },
}

export const sPadding = (left) =>
  left
    ? {
        [s.md]: {
          paddingRight: '0.75rem',
        },
      }
    : {
        [s.md]: {
          paddingLeft: '0.75rem',
        },
      }

const types = [
  {
    type: 'MESSAGE',
    icon: () => <MessageIcon />,
    name: {
      pl: 'Wycena kontenerów',
      en: 'Message',
      zh: 'Message',
    },
    title: {
      pl: 'Wycena kontenerów',
      en: 'Container Quote',
      zh: '集装箱报价',
    },
    desc: {
      pl: 'Szybka wycena kontenerów. Napisz do nas i wyceń swoje zamówienie.',
      en: 'Quick valuation of containers. Write to us and quote your order.',
      zh: '集装箱快速估价。联系我们，并就您的订单提供报价。',
    },
  },
]
const content = {
  sections: {
    cargos: {
      pl: 'Ładunki',
      en: 'Cargo',
      zh: 'Cargo',
    },
    contact: {
      pl: 'Informacje Kontaktowe',
      en: 'Contact Information',
      zh: 'Contact Information',
    },
  },
  fields: {
    message: {
      label: { pl: 'Wiadomość', en: 'Message', zh: '消息' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'message',
      isRequired: ['MESSAGE'],
    },
    email: {
      label: { pl: 'Email', en: 'Email', zh: '电子邮件' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'email',
      isRequired: ['MESSAGE', 'QUOTE', 'COLLAB', 'CARRIER'],
    },
    phone: {
      label: { pl: 'Telefon', en: 'Number', zh: '编号' },
      placeholder: { pl: '', en: '' },
      type: 'tel',
      isRequired: [],
    },
    firstName: {
      label: { pl: 'Imię', en: 'First name', zh: '名字' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'text',
      isRequired: [],
    },
    lastName: {
      label: { pl: 'Nazwisko', en: 'Last name', zh: '姓氏' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'text',
      isRequired: [],
    },
    company: {
      label: { pl: 'Firma', en: 'Company', zh: '公司' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'text',
      isRequired: ['CARRIER'],
    },
    nip: {
      label: { pl: 'NIP', en: 'VAT ID', zh: '增值税 ID' },
      placeholder: { pl: '', en: '', zh: '' },
      type: 'string',
      isRequired: ['MESSAGE', 'QUOTE', 'COLLAB', 'CARRIER'],
    },
  },
  dictonary: {
    success: {
      title: { pl: 'Dziękujemy!', en: 'Thank you!' },
      text: {
        pl: 'Twoja wiadomość została wysłana. Odpowiemy najszybciej jak to możliwe.',
        en: 'Your message has been sent. We will reply as soon as possible.',
        zh: '您的消息已发送。我们将尽快回复。',
      },
    },
    failure: {
      title: { pl: 'Niepowodzenie!', en: 'Failure!' },
      text: {
        pl: 'Wygląda na to, że Twoja wiadomość nie została wysłana. Skontaktuj się z nami drogą mailową lub telefonicznie',
        en: 'It looks like your message has not been sent. Contact us by e-mail or call us',
        zh: '您的消息似乎尚未发送。请通过电子邮件或致电联系我们',
      },
    },
    send: {
      pl: 'Wyślij',
      en: 'Send',
      zh: '发送',
    },
    fill: {
      pl: 'Wypełnij formularz',
      en: 'Fill up the form',
      zh: '填写表格',
    },
    fillagreements: {
      pl: 'Zaznacz zgodę',
      en: 'Agree to the terms',
      zh: '同意条款',
    },
  },
}

export default FormDynamic
